import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useHistory } from 'react-router-dom';
import { Button, Spinner } from 'components/elements';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  Enum_Subscription_Status,
  namedOperations,
  useCreateSubscriptionMutation,
  useGetMyAccountQuery,
  usePlansQuery,
  useUpdateSubscriptionMutation,
} from 'graphql/types';
import {
  CurrentPlanBanner,
  MembershipActions,
  MembershipHeader,
  MembershipSubtitle,
  MembershipTitle,
  MembershipWrapper,
  PlanBadge,
  PlanCard,
  PlanDescription,
  PlanFeatureItem,
  PlanFeatureList,
  PlanGrid,
  PlanName,
  PlanPrice,
} from './membership.styled';
import {
  matchPlanKey,
  MembershipPlanKey,
  resolveMembershipPlans,
  ResolvedMembershipPlan,
} from './membershipPlans';
import { BackButton } from 'containers/singleGroup/index.styled';

function formatPrice(price: number) {
  if (price <= 0) return 'Free';
  return `$${price.toFixed(2)} / month`;
}

export default function MembershipPlanPage() {
  const history = useHistory();
  const { data: accountData, loading: accountLoading } = useGetMyAccountQuery();
  const { data: plansData, loading: plansLoading } = usePlansQuery({
    variables: { sort: ['price:asc'] },
  });

  const [createSubscription, { loading: creating }] = useCreateSubscriptionMutation({
    refetchQueries: [namedOperations.Query.GetMyAccount],
  });
  const [updateSubscription, { loading: updating }] = useUpdateSubscriptionMutation({
    refetchQueries: [namedOperations.Query.GetMyAccount],
  });

  const accountAttr = accountData?.myAccount?.data?.attributes;
  const currentSub = accountAttr?.currentSubscription?.data;
  const currentPlanName = currentSub?.attributes?.plan?.data?.attributes?.name ?? 'Friends';
  const currentPlanKey = matchPlanKey(currentPlanName) ?? 'friends';

  const plans = useMemo(
    () => resolveMembershipPlans(plansData?.plans?.data ?? []),
    [plansData],
  );

  const [selectedKey, setSelectedKey] = useState<MembershipPlanKey>(currentPlanKey);

  useEffect(() => {
    setSelectedKey(currentPlanKey);
  }, [currentPlanKey]);

  const selectedPlan = plans.find((p) => p.key === selectedKey) as ResolvedMembershipPlan | undefined;
  const saving = creating || updating;
  const loading = accountLoading || plansLoading;
  const isCurrentSelection = selectedKey === currentPlanKey;

  const onSave = async () => {
    if (!selectedPlan?.id) {
      message.error('This plan is not available yet. Please contact support.');
      return;
    }

    try {
      if (currentSub?.id) {
        await updateSubscription({
          variables: {
            id: currentSub.id,
            data: {
              plan: selectedPlan.id,
              status: Enum_Subscription_Status.Active,
            },
          },
        });
      } else {
        await createSubscription({
          variables: {
            input: { plan: selectedPlan.id },
          },
        });
      }
      message.success(`Your membership is now ${selectedPlan.name}.`);
      history.push('/account/profile');
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  return (
    <MembershipWrapper>
      <MembershipHeader>
        <BackButton onClick={() => history.push('/account/profile')} style={{ paddingLeft: 0 }}>
          <ArrowLeftOutlined /> Back to settings
        </BackButton>

        <MembershipTitle>Supporter Levels</MembershipTitle>
        <MembershipSubtitle>
          Choose the plan that fits you. Friends, Zoroastrians, and Supporters — change anytime from this page.
        </MembershipSubtitle>
        <CurrentPlanBanner>Current plan: {currentPlanName}</CurrentPlanBanner>
      </MembershipHeader>

      {loading ? (
        <Spinner size="md" />
      ) : (
        <>
          <PlanGrid>
            {plans.map((plan) => {
              const selected = plan.key === selectedKey;
              const isCurrent = plan.key === currentPlanKey;
              const featured = plan.key === 'zoroastrians';

              return (
                <PlanCard
                  key={plan.key}
                  type="button"
                  $selected={selected}
                  $featured={featured}
                  disabled={!plan.id}
                  onClick={() => setSelectedKey(plan.key)}
                >
                  {isCurrent ? (
                    <PlanBadge style={{ background: '#725200' }}>Current</PlanBadge>
                  ) : (
                    featured && <PlanBadge>Popular</PlanBadge>
                  )}
                  <PlanName>{plan.name}</PlanName>
                  <PlanPrice>{formatPrice(plan.price)}</PlanPrice>
                  <PlanDescription>{plan.description}</PlanDescription>
                  <PlanFeatureList>
                    {plan.features.map((feature) => (
                      <PlanFeatureItem key={feature}>{feature}</PlanFeatureItem>
                    ))}
                  </PlanFeatureList>
                </PlanCard>
              );
            })}
          </PlanGrid>

          <MembershipActions>
            <Button type="primary" onClick={onSave} loading={saving} disabled={isCurrentSelection || !selectedPlan?.id}>
              {isCurrentSelection ? 'Current plan' : `Switch to ${selectedPlan?.name ?? 'plan'}`}
            </Button>
            <Button type="default" onClick={() => history.push('/account/profile')}>
              Cancel
            </Button>
          </MembershipActions>
        </>
      )}
    </MembershipWrapper>
  );
}
